import {
  type HeartbeatSessionDto,
  sessionsControllerHeartbeat,
  sessionsControllerOpen
} from 'api.gen'
import { useEffect, useRef } from 'react'

const SESSION_KEY = 'sessionId'
const INTERVAL = 15000
const IDLE_MS = 30000
const MAX_RETRY_ATTEMPTS = 3
const INITIAL_RETRY_DELAY = 1000

export function useSessionHeartbeat(enabled: boolean = true) {
  const lastTick = useRef(Date.now())
  const lastInteraction = useRef(Date.now())

  const activeNow = () => {
    const visible = document.visibilityState === 'visible'
    const recent = Date.now() - lastInteraction.current < IDLE_MS
    return visible && recent
  }

  useEffect(() => {
    if (!enabled) {
      return
    }
    const touch = () => {
      lastInteraction.current = Date.now()
    }

    const events = [
      'mousemove',
      'keydown',
      'scroll',
      'click',
      'touchstart',
      'visibilitychange'
    ]

    events.forEach(event => {
      addEventListener(event, touch, { passive: true })
    })

    const timeoutIds: NodeJS.Timeout[] = []
    const createCancellableSleep = (ms: number) => {
      return new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(resolve, ms)
        timeoutIds.push(timeoutId)
      })
    }

    const ensureSession = async (): Promise<string | null> => {
      const existingId = sessionStorage.getItem(SESSION_KEY)

      if (existingId) {
        return existingId
      }

      for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
        if (cancelled) {
          return null
        }

        try {
          const response = await sessionsControllerOpen()
          const sessionId = response.data.sessionId

          sessionStorage.setItem(SESSION_KEY, sessionId)
          return sessionId
        } catch (error) {
          console.warn(
            `Failed to initialize session (attempt ${
              attempt + 1
            }/${MAX_RETRY_ATTEMPTS}):`,
            error
          )

          sessionStorage.removeItem(SESSION_KEY)

          if (attempt < MAX_RETRY_ATTEMPTS - 1) {
            const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt)
            await createCancellableSleep(delay)
          }
        }
      }

      console.error('Failed to initialize session after all retry attempts')
      return null
    }

    const heartbeat = async (sessionId: string) => {
      const now = Date.now()
      const dt = Math.min(60000, Math.max(0, now - lastTick.current))
      lastTick.current = now

      const payload: HeartbeatSessionDto = {
        sessionId,
        dt_seconds: dt / 1000,
        active: activeNow()
      }

      try {
        await sessionsControllerHeartbeat(payload)
      } catch (error) {
        console.warn('Failed to send heartbeat:', error)
        sessionStorage.removeItem(SESSION_KEY)
      }
    }

    let intervalId: NodeJS.Timeout | null = null
    let pagehideRegistered = false
    let pagehideHandler: (() => void) | null = null
    let cancelled = false

    const runHeartbeat = async () => {
      const sessionId = sessionStorage.getItem(SESSION_KEY)

      if (!sessionId) {
        const newSessionId = await ensureSession()
        if (newSessionId && !cancelled) {
          await heartbeat(newSessionId)
        }
      } else {
        await heartbeat(sessionId)
      }
    }

    const initializeSession = async () => {
      const sessionId = await ensureSession()
      if (cancelled || !sessionId) return

      intervalId = setInterval(runHeartbeat, INTERVAL)
      pagehideHandler = () => runHeartbeat()
      addEventListener('pagehide', pagehideHandler, { capture: true })
      pagehideRegistered = true
    }

    initializeSession()

    return () => {
      cancelled = true

      timeoutIds.forEach(id => clearTimeout(id))
      timeoutIds.length = 0

      events.forEach(event => {
        removeEventListener(event, touch)
      })
      if (intervalId) {
        clearInterval(intervalId)
      }
      if (pagehideRegistered && pagehideHandler) {
        removeEventListener('pagehide', pagehideHandler, { capture: true })
      }
    }
  }, [enabled])
}
